import { AppHelper } from "../../appHelper.js";
import { AddressSearchColumn, BaseColumn, CheckBoxColumn, ComboBoxColumn, ComponentColumn, ContactSearchColumn, DataEntryColumn, DatePickerColumn, DropDownColumn, DropDownOnDemandColumn, field, MachineSearchColumn, MultiColumn, NumericColumn, ProjectSearchColumn, ResourceSearchColumn, Schema, SearchBaseColumn, TemplateColumn, TicketSearchColumn, TimeTextBox, ValidationResult, ValidationResultType } from "../components/editableGrid/editableGrid.js";
import { Helper } from "./helper.js";
import { HTTPService } from "./httpManager.js";

export class IngridHelper {
    private evalAction;
    private _context;
    private _appHelper: AppHelper;
    private _helperGuid: string;

    constructor(context) {
        this._context = context;
        this._appHelper = new AppHelper();
        this._appHelper.init(context);
        this.evalAction = this._appHelper.evalAction;
        this._helperGuid = kendo.guid();
        if (!window.hasOwnProperty("evalAction"))
            window["evalAction"] = {};
        window["evalAction"][this._helperGuid] = this.evalAction;
    }

    public initGridSettings = async (metaData: any, schema: Schema = null): Promise<{ Schema: Schema, Columns: BaseColumn[] }> => {
        const columns: BaseColumn[] = [];
        if (!schema)
            schema = new Schema();

        if (Helper.hasValue(metaData.dataSource)) {
            schema.group = metaData.dataSource.group;
            schema.aggregate = metaData.dataSource.aggregate;
        }

        await this.mapMetaDataToGridSettings(columns, schema, metaData);
        return { Columns: columns, Schema: schema };
    }

    private async mapMetaDataToGridSettings(columns: BaseColumn[], schema: Schema, metaData) {
        for (let i = 0; i < metaData.columns.length; i++) {
            let metaDataField: any = metaData.columns[i];
            if (!metaDataField.type)
                metaDataField.type = "textBox";
            let visible = this.evalAction(metaDataField.visible);
            if (Helper.hasValue(visible) && !visible)
                continue;

            await this.addColumn(columns, metaDataField);
            this.addSchema(schema, metaDataField);
        }
    }

    private addSchema(schema: Schema, metaDataField) {
        if (Helper.hasValue(metaDataField.field)) {
            let fieldName = this.getFieldName(metaDataField.field);
            let validation = this.getValidation(metaDataField);
            schema.fields[fieldName] = new field(this.convertTypeForGrid(metaDataField.type), null, validation);
        }
        if (Helper.hasValue(metaDataField.columns)) {
            for (let column of metaDataField.columns)
                this.addSchema(schema, column);
        }
    }

    private getValidation(metaDataField) {
        let min = this.evalAction(metaDataField.min);
        let max = this.evalAction(metaDataField.max);
        let maxLength = this.evalAction(metaDataField.maxLength);

        let validation = {};
        if (Helper.hasValue(min))
            validation["min"] = min;
        if (Helper.hasValue(max))
            validation["max"] = max;
        if (Helper.hasValue(maxLength))
            validation["maxLength"] = maxLength;

        let required = false;
        try {
            required = this.evalAction(metaDataField.required);
        } catch {
            required = false;
        }
        if (Helper.hasValue(required) && typeof required == "boolean" && required) {
            let requiredMessage = Helper.getTranslation("WebSMS.InGrid.RequiredMessage").replace("{title}", metaDataField.title);
            validation["required"] = { message: requiredMessage };
        }
        return validation;
    }

    private async addColumn(columns: BaseColumn[], metaDataField) {
        let column: BaseColumn = await this.getColumn(metaDataField.type, metaDataField);
        columns.push(column)
    }

    private getUrl(metadata: dropDownFieldMetaData): string {
        let url = this.addQueryStringParameters(metadata);
        url = this.eval(url);
        url = this.encodeQueryStringParamter(url);
        return url;
    }

    private encodeQueryStringParamter = (url: string) => {
        let params = Helper.getQueryStringParameters(url);
        let urlWithoutParams = url.split('?')[0];
        return Helper.addQueryStringParameters(urlWithoutParams, params);
    }

    private addQueryStringParameters(metadata: dropDownFieldMetaData, dataItem = null): string {
        let url = metadata.dataSourceURL;
        if (!Helper.isNullOrEmpty(metadata.parameters)) {
            const params: any = {};
            for (let key of Object.keys(metadata.parameters)) {
                const value = this.evalAction(metadata.parameters[key], { row: dataItem });
                params[key] = value;
            }
            url = Helper.addQueryStringParameters(url, params);
        }
        return url;
    }

    private getFieldName(field: string): string {
        let fieldSplit = field.split(".");
        let name = "";
        if (field.includes("IndividualFields."))
            name = "Ind_";
        if (fieldSplit.length > 1) {
            name += fieldSplit[fieldSplit.length - 1];
        }
        else {
            name = field;
        }
        return name;
    }

    private async getColumn(type: string, metadata: baseFieldMetaData): Promise<BaseColumn> {
        type = type.toLowerCase();
        let column: DataEntryColumn = new DataEntryColumn();
        if (type == ComponentType.MachineSearch) {
            column = new MachineSearchColumn();
        }
        else if (type == ComponentType.AddressSearch) {
            column = new AddressSearchColumn();
        }
        else if (type == ComponentType.ProjectSearch) {
            column = new ProjectSearchColumn();
        }
        else if (type == ComponentType.TicketSearch) {
            column = new TicketSearchColumn();
        }
        else if (type == ComponentType.ResourceSearch) {
            column = new ResourceSearchColumn();
        }
        else if (type == ComponentType.ContactSearch) {
            column = new ContactSearchColumn();
        }
        else if (type == ComponentType.CheckBox) {
            column = new CheckBoxColumn();
        }
        else if (type == ComponentType.TimeTextBox) {
            column = new TimeTextBox();
        }
        else if (type == ComponentType.DatePicker) {
            column = new DatePickerColumn();
            column.format = "{0:d}";
        }
        else if (type == ComponentType.Numeric) {
            column = new NumericColumn();
        }
        else if (type == ComponentType.DropDown) {
            column = await this.getDropDownColumn(metadata as dropDownFieldMetaData);
        }
        else if (type == ComponentType.DropDownOnDemand) {
            column = this.getDropDownOnDemandColumn(metadata as dropDownOnDemandFieldMetaData);
        }
        else if (type == ComponentType.ComboBox) {
            column = await this.getComboBoxColumn(metadata as dropDownFieldMetaData);
        }
        else if (type == ComponentType.IsSearch) {
            column = this.getIsSearchColumn(metadata as searchFieldMetaData);
        }
        else if (type == ComponentType.Command) {
            column = this.getCommandColumn(metadata as commandMetaData);
        }
        else if (type == ComponentType.Component) {
            column = this.getComponentColumn(metadata as componentMetaData);
        }
        else if (type == ComponentType.MultiColumn) {
            return await this.getMultiColumn(metadata as multiColumnFieldMetaData);
        }

        this.mapMetaDataToColumn(column, metadata as baseFieldMetaData);
        return column;
    }

    private mapMetaDataToColumn(column: DataEntryColumn, metaData: baseFieldMetaData) {
        column.type = metaData.type.toLowerCase();
        column.editable = (dataItem) => !this.evalAction(metaData.readonly, { row: dataItem });
        column.field = metaData.field;
        column.title = metaData.title;
        if (!Helper.isNullOrEmpty(metaData.format))
            column.format = metaData.format;
        column.width = metaData.width;
        column.defaultValue = (dataItem) => this.evalAction(metaData.defaultValue, { row: dataItem });
        column.required = (dataItem) => this.evalAction(metaData.required, { row: dataItem });
        column.headerTemplate = metaData.headerTemplate;
        column.groupHeaderTemplate = metaData.groupHeaderTemplate;
        column.groupHeaderColumnTemplate = metaData.groupHeaderColumnTemplate;
        column.template = metaData.template;
        if (Helper.hasValue(metaData.template) && Array.isArray(metaData.template)) {
            column.template = metaData.template.reduce((acc, curr) => acc + curr, "");
        }
        column.aggregates = metaData.aggregates;
        column.multiLine = metaData.multiLine;
        column.multiLines = metaData.multiLines;
        if (Helper.hasValue(metaData.filterable)) {
            switch (typeof metaData.filterable) {
                case "object":
                    column.filterable = metaData.filterable;
                    break;
                case "boolean":
                    column.filterable = this.evalAction(metaData.filterable);
                    break;
            }
        }
        column.media = metaData.media;
    }

    private async getDropDownColumn(metadata: dropDownFieldMetaData): Promise<DropDownColumn> {
        const column = new DropDownColumn();
        column.useDataSource = !Helper.hasValue(metadata.dataSourceURL) && Helper.hasValue(metadata.dataSource);
        column.choices = await this.getChoices(metadata);
        column.getChoices = () => this.getChoisesDataSource(metadata);
        column.listWidth = metadata.listWidth;
        column.itemTemplate = metadata.itemTemplate;
        column.valueTemplate = metadata.valueTemplate;
        column.filter = metadata.filter;
        column.optionLabel = Helper.hasValue(metadata.optionLabel) ? metadata.optionLabel : " ";
        column.fallbackTextField = metadata.fallbackTextField;
        return column;
    }

    private async getComboBoxColumn(metadata: dropDownFieldMetaData): Promise<ComboBoxColumn> {
        const column = new ComboBoxColumn();
        column.useDataSource = !Helper.hasValue(metadata.dataSourceURL) && Helper.hasValue(metadata.dataSource);
        column.choices = await this.getChoices(metadata);
        column.getChoices = () => this.getChoisesDataSource(metadata);
        column.listWidth = metadata.listWidth;
        column.itemTemplate = metadata.itemTemplate;
        column.fallbackTextField = metadata.fallbackTextField;
        return column;
    }

    private getDropDownOnDemandColumn(metadata: dropDownOnDemandFieldMetaData): DropDownOnDemandColumn {
        const column = new DropDownOnDemandColumn();
        column.lookUpField = metadata.lookUpField;
        column.getChoices = (dataItem) => {
            let url = column.getUrl(dataItem);
            column.evalUrl = url;
            return this.loadChoices(url, metadata.dataValueField, metadata.dataTextField);
        }
        column.getUrl = (dataItem) => {
            let evalUrl = this.addQueryStringParameters(metadata, dataItem);
            evalUrl = this.eval(evalUrl, dataItem);
            evalUrl = this.encodeQueryStringParamter(evalUrl);
            return evalUrl;
        }
        column.listWidth = metadata.listWidth;
        column.itemTemplate = metadata.itemTemplate;
        column.valueTemplate = metadata.valueTemplate;
        column.optionLabel = metadata.optionLabel ?? " ";
        return column;
    }

    private async getMultiColumn(metaData: multiColumnFieldMetaData): Promise<MultiColumn> {
        let multiColumn = new MultiColumn;
        multiColumn.title = metaData.title;
        multiColumn.field = metaData.field;
        multiColumn.type = metaData.type.toLowerCase();
        multiColumn.columns = [];
        for (let columnMetaData of metaData.columns) {
            let column = await this.getColumn(columnMetaData.type, columnMetaData);
            multiColumn.columns.push(column);
        }
        return multiColumn;
    }

    private getCommandColumn(metaData: commandMetaData): BaseColumn {
        const column = new BaseColumn();
        column.commands = [];
        for (let command of metaData.commands) {
            let commandItem = this.getCommandItem(command.text, command.iconClass, command.className, command.visible);
            commandItem.click = (e) => {
                let action = this.evalAction(command.click);
                action(e);
            };
            column.commands.push(commandItem);
        }
        return column;
    }

    private getComponentColumn(metaData: componentMetaData): BaseColumn {
        const evalProps = (props, dataItem) => {
            Object.keys(props).forEach(key => {
                if (typeof props[key] === 'object' && props[key] !== null) {
                    evalProps(props[key], dataItem)
                } else {
                    if (key == "visible")
                        props[key] = this.evalAction(props[key], { row: dataItem });
                    else
                        props[key] = this.eval(props[key], dataItem);
                }
            })
            return props;
        }

        let column = new ComponentColumn();
        column.visible = (dataItem) => metaData.visible && this.evalAction(metaData.visible, { row: dataItem });
        column.iconName = metaData.iconName;
        column.moduleName = metaData.moduleName;
        column.helpUrl = metaData.helpUrl;
        column.props = (dataItem) => evalProps(JSON.parse(JSON.stringify(metaData.props)), dataItem);

        if (!metaData.template) {
            metaData.template = `<div title='{{{WebCRM.Tasks.WorkPackage.Show}}}'><i class='fal fa-${metaData.iconName}' style='margin-right: 0.5em;'></i></div>`
        }
        metaData.readonly = true;
        return column;
    }

    private getCommandItem(text: string, iconClass: string, className: string, visibleCondition: any): kendo.ui.GridColumnCommandItem {
        let guid = kendo.guid();
        //if (template) {
        //    let onClick = `<a class='k-button k-grid-${guid}'><span class='${iconClass}'>${iconClass ? '' : text}</span></a>`;
        //    template = template.replace("{onClick}", onClick);
        //}
        let commandItem: kendo.ui.GridColumnCommandItem = {
            name: guid, //Name muss getzt sein
            text: iconClass ? '' : text,
            visible: (dataItem) => this.evalAction(visibleCondition, { row: dataItem }),
            iconClass: iconClass,
            //template: template,
            className: className
        }
        return commandItem;
    }

    private getIsSearchColumn(metaData: searchFieldMetaData): SearchBaseColumn {
        const column = new SearchBaseColumn;
        column.searchWindowUrl = this.eval(metaData.searchWindowUrl);
        column.dataTextField = metaData.dataTextField;
        column.dataValueField = metaData.dataValueField;
        column.searchValueField = metaData.searchValueField;
        column.searchTextField = metaData.searchTextField;
        column.autocompleteEnabled = metaData.autocompleteEnabled;
        column.autocompleteDataTextField = metaData.autocompleteDataTextField;
        column.autocompleteUrl = this.eval(metaData.autocompleteUrl);
        column.autocompleteTemplate = metaData.autocompleteTemplate;
        column.template = metaData.template;
        column.readOnlyTextField = metaData.readOnlyTextField;
        column.fullObjectKey = metaData.fullObjectKey;
        column.deleteButtonEnabled = metaData.deleteButtonEnabled;
        return column;
    }

    private convertTypeForGrid(typeString: string) {
        typeString = typeString.toLowerCase();
        switch (typeString) {
            case "numerictextbox":
            case "timetextbox":
                return "number";
            case "checkbox":
                return "boolean";
            case "datepicker":
                return "date";
            case "textbox":
            case "combobox":
            case "dropdownondemand":
            default:
                return "string";
        }
    }

    private async getChoices(metadata: any): Promise<any> {
        let choices;
        if (!Helper.isNullOrEmpty(metadata.dataSourceURL)) {
            let url = this.getUrl(metadata);
            choices = await this.loadChoices(url, metadata.dataValueField, metadata.dataTextField);
        }
        else if (!Helper.isNullOrEmpty(metadata.dataSource)) {
            choices = this.getChoisesDataSource(metadata);
        }
        return choices;
    }

    private async loadChoices(url: string, dataValueField: string, dataTextField: string): Promise<{ Value: any; Text: any; }[]> {
        let lookUp = await HTTPService.get<any>(url);
        let lookUps = lookUp.map((i) => {
            return typeof i !== "object" ? { Value: i, Text: i } : { Value: i[dataValueField], Text: i[dataTextField] }
        });
        return lookUps;
    }

    private getChoisesDataSource(metadata: dropDownFieldMetaData): { Value: any; Text: any; }[] {
        let lookUp = this.evalAction("dataSources." + metadata.dataSource, { dataSources: this._context.props.dataSources });
        let lookUps = lookUp.map((i) => {
            return typeof i !== "object" ? { Value: i, Text: i } : { Value: i[metadata.dataValueField], Text: i[metadata.dataTextField] }
        });
        return lookUps;
    }

    private eval(value: string, row = null) {

        let evalPart = (value: string, regex: string): string => {
            let evalValue = value;
            if (!Helper.hasValue(evalValue))
                return;
            while (typeof evalValue === 'string' && evalValue.match(regex) !== null) {
                let match = evalValue.match(regex);
                let value = this.evalAction(match[0], { row: row });
                evalValue = evalValue.replace(match[0], value);
            }
            return evalValue;
        }

        value = evalPart(value, "entity.[a-zA-Z0-9.]*");
        value = evalPart(value, "Innosoft.[a-zA-Z0-9.]*");
        if (Helper.hasValue(row))
            value = evalPart(value, "row.[a-zA-Z0-9.]*");
        return value;
    }
}

interface baseFieldMetaData {
    type: string;
    field: string;
    title: string;
    width: string;
    visible: string;
    readonly: boolean;
    format: string;
    defaultValue: string;
    required: string;
    min: any;
    max: any;
    template: string;
    groupHeaderTemplate: string;
    groupHeaderColumnTemplate: string;
    headerTemplate: string;
    aggregates: string[];
    multiLine: boolean;
    multiLines: number;
    filterable: boolean;
    media: string;
}

interface dropDownFieldMetaData extends baseFieldMetaData {
    dataSource: string;
    dataSourceURL: string;
    dataTextField: string;
    dataValueField: string;
    parameters: any;
    listWidth: number;
    itemTemplate: string;
    valueTemplate: string;
    filter?: string;
    optionLabel?: string;
    fallbackTextField?: string;
}

interface dropDownOnDemandFieldMetaData extends dropDownFieldMetaData {
    lookUpField: string;
}

interface multiColumnFieldMetaData extends baseFieldMetaData {
    columns: baseFieldMetaData[];
}

interface commandMetaData extends baseFieldMetaData {
    commands: [
        {
            text: string,
            click: string,
            visible: string,
            iconClass: string,
            template: string,
            className: string
        }
    ];
}

interface componentMetaData extends baseFieldMetaData {
    iconName: string;
    className: string;
    moduleName: string;
    helpUrl: string;
    props: any;
}

interface searchFieldMetaData extends baseFieldMetaData {
    searchWindowUrl: string;
    dataTextField: string;
    dataValueField: string;
    searchValueField: string;
    searchTextField: string;
    autocompleteEnabled: boolean;
    autocompleteDataTextField: string;
    autocompleteUrl: string;
    autocompleteTemplate: string;
    readOnlyTextField: boolean;
    fullObjectKey: string;
    deleteButtonEnabled: boolean;
}

export enum ComponentType {
    EditField = "textbox",
    Numeric = "numerictextbox",
    CheckBox = "checkbox",
    ComboBox = "combobox",
    DropDown = "dropdown",
    DropDownOnDemand = "dropdownondemand",
    DatePicker = "datepicker",
    TimeTextBox = "timetextbox",
    MultiColumn = "multicolumn",
    IsSearch = "issearch",
    AddressSearch = "addresssearch",
    MachineSearch = "machinesearch",
    ProjectSearch = "projectsearch",
    ResourceSearch = "resourcesearch",
    ContactSearch = "contactsearch",
    TicketSearch = "ticketsearch",
    Command = "command",
    Component = "component"
}